import { authInfo } from "@/core/auth";
import { apiCallCustom, Response as APIResponse } from "@/core/api";
import { connectWebsocket } from "@/flow/services/flow.service";
import type { Client, Message, Subscription } from "webstomp-client";

const WIZARD_BASE_URL = "";
const WIZARD_ENDPOINT_PREFIX = "/rest/v1/wizard";
const WIZARD_STOMP_DESTINATION = "/wizard";
// Exported so other wizard modules can key off the same request-timeout
// convention (e.g. WizardDrawer.vue's stale-processing UI timeout) instead
// of re-declaring the number and risking the two silently drifting apart.
export const WIZARD_TIMEOUT_MS = 300000;

// The internal instruction seeded for auto-debug (Activities) analyses. The
// backend legitimately stores it as the session's first user turn, so it can
// leak back out anywhere raw session/message data is surfaced (chat bubbles,
// restored history, the session-list preview) — every such surface must run
// its text through isAutoDebugSeedText/filter it via this same prefix.
export const WIZARD_AUTO_DEBUG_SEED_PREFIX =
  "A flow run just failed and I need you to diagnose it.";

// GET /wizard/sessions builds its `preview` from the raw backend `transcript`
// field, not the same clean `conversation_history` restoreConversation reads —
// `transcript` also captures the debug_agent tool's own internal sub-agent
// invocation prompt (see debug_agent() in ai/wizard/core/tools/agent_tools.py),
// which becomes the "first user message" there even though it never appears
// in the actual conversation. This is a second, backend-controlled prefix we
// have no way to derive dynamically, so it's matched by its known literal
// text as a pragmatic stopgap — the durable fix belongs server-side (build
// `preview` from the same source as conversation_history).
const WIZARD_DEBUG_AGENT_TOOL_PREFIX = "Diagnose the failed run. activity_id=";

export function isAutoDebugSeedText(text: unknown): boolean {
  return (
    typeof text === "string" &&
    (text.startsWith(WIZARD_AUTO_DEBUG_SEED_PREFIX) ||
      text.startsWith(WIZARD_DEBUG_AGENT_TOOL_PREFIX))
  );
}

const WIZARD_PHASE_STATUS_MESSAGES: Record<string, string> = {
  intake: "Listening",
  guard_check: "Checking",
  processing: "Brewing",
  connector_selection: "Matching",
  action_construction: "Building",
  awaiting_confirmation: "Reviewing",
  completed: "Ready",
  error: "Troubleshooting",
  waiting_for_user: "Waiting",
  intake_phase: "Listening",
  connector_phase: "Matching",
  action_phase: "Building",
  flow_generation_phase: "Shaping",
};

function getWizardHeaders() {
  const token = authInfo.value?.accessToken || "";
  const tenantId = authInfo.value?.tenantId || "";
  const organizationId = authInfo.value?.organizationId || "";
  const userId = authInfo.value?.userId || "";

  if (!token || !tenantId || !organizationId || !userId) {
    throw new Error(
      "Wizard authentication context is incomplete. " +
        "Please log in and select an organization."
    );
  }

  return {
    Authorization: token,
    "X-Tenant-ID": tenantId,
    "X-Organization-ID": organizationId,
    "X-User-ID": userId,
    "Content-Type": "application/json",
  };
}

export interface WizardAttachment {
  id: string;
  name: string;
  contentType: string;
  size: number;
  kind: "image" | "document";
}

export interface WizardMessage {
  role: "user" | "assistant" | "mode_divider";
  content: string;
  timestamp?: string;
  /** Only set when role === "mode_divider" */
  toMode?: WizardMode;
  /** Files attached to this message, if any */
  attachments?: WizardAttachment[];
}

/**
 * Copy for the in-chat prompt that asks whether to open the connection form.
 * The form is a modal over the whole chat, so the wizard asks first and opens it
 * only once the user agrees. The question is all it carries: the assistant's
 * message above it already explains what is missing, and the interrupt's own
 * prompt is shown inside the form once it opens.
 */
export interface WizardConnectionPrompt {
  /** The question put to the user, e.g. "No valid connection was found. ...". */
  question: string;
}

export interface WizardClientConfig {
  messageMaxChars: number;
  pasteToFileThreshold: number;
  attachmentMaxFileBytes: number;
  attachmentMaxPerMessage: number;
  attachmentAllowedContentTypes: string[];
}

export type WizardMode = "wizard" | "chat";

export interface WizardSessionResponse {
  sessionId: string;
  status: WizardSessionStatus;
  message: string;
  currentPhase?: string;
  result?: any;
}

export type WizardSessionStatus =
  | "processing"
  | "waiting_for_user"
  | "completed"
  | "error"
  | "cancelled";

export interface WizardStatusResponse {
  sessionId: string;
  status: string;
  currentPhase: string;
  progressPercentage: number;
  isComplete: boolean;
  errorMessage?: string;
  metadata?: any;
}

/**
 * How full the wizard's own working memory is, and how far from auto-compaction.
 *
 * Every figure comes from the compaction middleware that governs the thread, not
 * from a second count made for display — a meter that disagrees with the threshold
 * it describes is worse than no meter.
 *
 * `available` false means this thread has no ceiling configured, which is not the
 * same as an empty conversation. The two must not render alike.
 */
export interface WizardContextLevel {
  sessionId: string;
  available: boolean;
  usedTokens: number;
  usableTokens: number;
  compactAtTokens: number;
  tokensUntilCompaction: number;
  /** usedTokens / usableTokens, already clamped to 1 by the backend. */
  fraction: number;
  keepMessages: number;
  messageCount: number;
  /** False when the figure is a local estimate because the provider omitted usage. */
  providerBacked: boolean;
  reportedInputTokens: number | null;
  /** Thinking tokens from the last call. Informational — never part of usedTokens. */
  reasoningTokens: number | null;
}

export interface WizardCompactionResult {
  sessionId: string;
  /** False when nothing was ahead of the preserved tail — a normal no-op. */
  compacted: boolean;
  compactedThreads: string[];
  skippedThreads: string[];
  /** Threads left unchanged, mapped to why. These still hold their full history. */
  failedThreads: Record<string, string>;
  context: WizardContextLevel;
}

export interface WizardConversationResponse {
  sessionId: string;
  userIntent: string;
  conversationHistory: WizardMessage[];
  currentPhase: string;
}

export interface WizardSessionListItem {
  sessionId: string;
  status: string;
  currentPhase?: string;
  mode: WizardMode;
  preview: string;
  createdAt?: string;
  updatedAt?: string;
  flowId?: string;
  flowVersion?: number;
  flowName?: string;
}

export interface WizardSessionListResponse {
  sessions: WizardSessionListItem[];
  count: number;
}

export interface WizardStreamMessage {
  sessionId: string;
  status: WizardSessionStatus;
  message: string;
  timestamp?: string;
  data?: Record<string, any>;
}

export interface WizardStreamConnection {
  client: Client;
  subscription: Subscription;
  disconnect: () => void;
}

export interface WizardFlowContext {
  flowId: string;
  flowVersion: number;
  flowName?: string;
}

export interface WizardCurrentFlowSnapshot {
  [key: string]: any;
}

// Passed to WizardDrawer by the Activities host to auto-start a one-shot debug
// analysis of a failed run. `activityId` is the failed run id (== flowLogId).
export interface WizardAutoDebugContext {
  activityId: string;
  status?: string;
}

// The run a debug session is anchored to. Sent as a structured field rather than left to the
// seed message's prose: the backend persists it on the session and re-injects it on every turn,
// so the run id outlives the transcript replay window and cold starts (AI-268).
export interface WizardDebugContextPayload {
  activityId: string;
  flowId?: string;
  flowVersion?: number;
}

export function buildDebugContextPayload(
  debugContext?: WizardAutoDebugContext | null,
  flowContext?: WizardFlowContext | null
): WizardDebugContextPayload | undefined {
  const activityId = debugContext?.activityId?.trim();
  if (!activityId) return undefined;
  return {
    activityId,
    ...(flowContext?.flowId ? { flowId: flowContext.flowId } : {}),
    ...(flowContext?.flowVersion !== undefined &&
    flowContext?.flowVersion !== null
      ? { flowVersion: flowContext.flowVersion }
      : {}),
  };
}

// camelCase in TS, snake_case on the wire — same boundary mapping the flow context uses.
function debugContextBody(debugContext?: WizardDebugContextPayload | null) {
  if (!debugContext) return {};
  return {
    debug_context: {
      activity_id: debugContext.activityId,
      ...(debugContext.flowId ? { flow_id: debugContext.flowId } : {}),
      ...(debugContext.flowVersion !== undefined
        ? { version: debugContext.flowVersion }
        : {}),
    },
  };
}

export type PersistedWizardFlowAction = "created" | "updated";

export interface PersistedWizardFlowResult {
  id: string;
  version: number;
  name: string;
  action: PersistedWizardFlowAction;
}

function mapWizardSessionResponse(
  data: Record<string, any>
): WizardSessionResponse {
  return {
    sessionId: data.session_id,
    status: data.status,
    message: data.message,
    currentPhase: data.current_phase,
    result: data.result,
  };
}

function mapWizardContextLevel(data: Record<string, any>): WizardContextLevel {
  return {
    sessionId: data.session_id,
    available: Boolean(data.available),
    usedTokens: data.used_tokens ?? 0,
    usableTokens: data.usable_tokens ?? 0,
    compactAtTokens: data.compact_at_tokens ?? 0,
    tokensUntilCompaction: data.tokens_until_compaction ?? 0,
    fraction: data.fraction ?? 0,
    keepMessages: data.keep_messages ?? 0,
    messageCount: data.message_count ?? 0,
    providerBacked: Boolean(data.provider_backed),
    reportedInputTokens: data.reported_input_tokens ?? null,
    reasoningTokens: data.reasoning_tokens ?? null,
  };
}

function mapWizardCompactionResult(
  data: Record<string, any>
): WizardCompactionResult {
  return {
    sessionId: data.session_id,
    compacted: Boolean(data.compacted),
    compactedThreads: data.compacted_threads ?? [],
    skippedThreads: data.skipped_threads ?? [],
    failedThreads: data.failed_threads ?? {},
    context: mapWizardContextLevel(data.context ?? {}),
  };
}

function mapWizardStatusResponse(
  data: Record<string, any>
): WizardStatusResponse {
  return {
    sessionId: data.session_id,
    status: data.status,
    currentPhase: data.current_phase,
    progressPercentage: data.progress_percentage,
    isComplete: data.is_complete,
    errorMessage: data.error_message,
    metadata: data.metadata,
  };
}

function mapWizardAttachment(data: Record<string, any>): WizardAttachment {
  return {
    id: data.id,
    name: data.name,
    contentType: data.content_type,
    size: data.size,
    kind: data.kind === "image" ? "image" : "document",
  };
}

function mapWizardMessage(data: Record<string, any>): WizardMessage {
  return {
    role: data.role,
    content: data.content,
    timestamp: data.timestamp,
    ...(Array.isArray(data.attachments) && data.attachments.length
      ? { attachments: data.attachments.map(mapWizardAttachment) }
      : {}),
  };
}

function mapWizardConversationResponse(
  data: Record<string, any>
): WizardConversationResponse {
  return {
    sessionId: data.session_id,
    userIntent: data.user_intent,
    conversationHistory: Array.isArray(data.conversation_history)
      ? data.conversation_history.map(mapWizardMessage)
      : data.conversation_history,
    currentPhase: data.current_phase,
  };
}

function mapWizardSessionListResponse(
  data: Record<string, any>
): WizardSessionListResponse {
  return {
    sessions: Array.isArray(data.sessions)
      ? data.sessions.map((session) => ({
          sessionId: session.session_id,
          status: session.status,
          currentPhase: session.current_phase,
          mode: session.mode,
          preview: session.preview,
          createdAt: session.created_at,
          updatedAt: session.updated_at,
          flowId: session.flow_id,
          flowVersion: session.flow_version,
          flowName: session.flow_name,
        }))
      : [],
    count: typeof data.count === "number" ? data.count : 0,
  };
}

function toWizardEndpoint(path: string): string {
  return `${WIZARD_ENDPOINT_PREFIX}${path}`;
}

function getWizardErrorMessage(response: APIResponse): string {
  return (
    response.result?.detail ||
    response.result?.message ||
    response.message ||
    "Wizard request failed"
  );
}

function unwrapWizardResponseData(response: APIResponse): Record<string, any> {
  if (response.code >= 400) {
    const error = new Error(getWizardErrorMessage(response)) as Error & {
      response?: {
        data: any;
        status: number;
      };
    };
    error.response = {
      data: response.result,
      status: response.code,
    };
    throw error;
  }

  return response.result;
}

async function wizardRequest(
  method: "GET" | "POST",
  path: string,
  options: {
    body?: any;
    params?: any;
  } = {}
): Promise<Record<string, any>> {
  const headers = getWizardHeaders();
  const response = await apiCallCustom(method, toWizardEndpoint(path), {
    baseURL: WIZARD_BASE_URL,
    body: options.body,
    headers,
    params: options.params,
    timeoutMs: WIZARD_TIMEOUT_MS,
  });

  return unwrapWizardResponseData(response);
}

export async function createWizardSession(
  userIntent: string,
  mode: WizardMode = "wizard",
  sessionId?: string,
  flowContext?: WizardFlowContext,
  currentFlowSnapshot?: WizardCurrentFlowSnapshot | null,
  skillGenerationEnabled?: boolean,
  attachmentIds?: string[],
  debugContext?: WizardDebugContextPayload | null
): Promise<WizardSessionResponse> {
  const response = await wizardRequest("POST", "/sessions", {
    body: {
      user_intent: userIntent,
      time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      mode,
      ...(sessionId ? { session_id: sessionId } : {}),
      ...(flowContext
        ? {
            flow_id: flowContext.flowId,
            flow_version: flowContext.flowVersion,
            flow_name: flowContext.flowName,
          }
        : {}),
      ...(currentFlowSnapshot
        ? { current_flow_snapshot: currentFlowSnapshot }
        : {}),
      ...(skillGenerationEnabled !== undefined
        ? { skill_generation_enabled: skillGenerationEnabled }
        : {}),
      ...(attachmentIds && attachmentIds.length
        ? { attachment_ids: attachmentIds }
        : {}),
      ...debugContextBody(debugContext),
    },
  });
  return mapWizardSessionResponse(response);
}

export async function sendWizardMessage(
  sessionId: string,
  message: string,
  mode?: WizardMode,
  currentFlowSnapshot?: WizardCurrentFlowSnapshot | null,
  skillGenerationEnabled?: boolean,
  attachmentIds?: string[],
  debugContext?: WizardDebugContextPayload | null
): Promise<WizardSessionResponse> {
  const response = await wizardRequest(
    "POST",
    `/sessions/${sessionId}/messages`,
    {
      body: {
        user_message: message,
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        mode,
        ...(currentFlowSnapshot
          ? { current_flow_snapshot: currentFlowSnapshot }
          : {}),
        ...(skillGenerationEnabled !== undefined
          ? { skill_generation_enabled: skillGenerationEnabled }
          : {}),
        ...(attachmentIds && attachmentIds.length
          ? { attachment_ids: attachmentIds }
          : {}),
        ...debugContextBody(debugContext),
      },
    }
  );
  return mapWizardSessionResponse(response);
}

/**
 * Upload a single attachment for a session. Returns metadata used to render the
 * chip; the file is referenced by id when the message is sent.
 */
export async function uploadWizardAttachment(
  sessionId: string,
  file: File
): Promise<WizardAttachment> {
  const formData = new FormData();
  formData.append("file", file, file.name);

  // Drop the JSON Content-Type so the browser sets the multipart boundary.
  const headers = getWizardHeaders();
  delete (headers as Record<string, string>)["Content-Type"];

  const response = await apiCallCustom(
    "POST",
    toWizardEndpoint(`/sessions/${sessionId}/attachments`),
    {
      baseURL: WIZARD_BASE_URL,
      body: formData,
      headers,
      timeoutMs: WIZARD_TIMEOUT_MS,
    }
  );
  return mapWizardAttachment(unwrapWizardResponseData(response));
}

/** A usable URL for an attachment, plus an optional cleanup for object URLs. */
export interface WizardAttachmentUrl {
  url: string;
  /** Revokes the object URL when one was created (local-mode fallback). */
  revoke?: () => void;
}

/**
 * Thrown when an attachment's metadata still exists but its stored blob is gone
 * (backend returns 410 Gone — e.g. removed by retention). Callers should tell the
 * user the file is no longer available and to upload it again, rather than the
 * generic "try again" download error.
 */
export class WizardAttachmentExpiredError extends Error {
  constructor(message = "Attachment is no longer available") {
    super(message);
    this.name = "WizardAttachmentExpiredError";
  }
}

/**
 * Resolve a usable download URL for an attachment's original file.
 *
 * In deployed (S3) environments the backend returns a presigned URL that can be
 * used directly as a download link. In local (no-bucket) mode the `/url`
 * endpoint returns 404, so we fall back to streaming the bytes and wrapping them
 * in an object URL (caller must call `revoke()` to free it).
 *
 * Throws {@link WizardAttachmentExpiredError} when the backend reports the blob
 * is gone (410).
 */
export async function getWizardAttachmentUrl(
  sessionId: string,
  attachmentId: string
): Promise<WizardAttachmentUrl> {
  const headers = getWizardHeaders();
  const urlResponse = await apiCallCustom(
    "GET",
    toWizardEndpoint(`/sessions/${sessionId}/attachments/${attachmentId}/url`),
    {
      baseURL: WIZARD_BASE_URL,
      headers,
      timeoutMs: WIZARD_TIMEOUT_MS,
    }
  );

  if (urlResponse.code < 400) {
    const presigned = unwrapWizardResponseData(urlResponse)?.url;
    if (presigned) {
      return { url: presigned as string };
    }
  } else if (urlResponse.code === 410) {
    // Blob gone (e.g. retention) — don't fall back to the byte endpoint (it would
    // also 410); surface the expired state so the user is told to re-upload.
    throw new WizardAttachmentExpiredError();
  } else if (urlResponse.code !== 404) {
    throw new Error(getWizardErrorMessage(urlResponse));
  }

  // Local-mode fallback: stream the bytes and wrap them in an object URL.
  const blobResponse = await apiCallCustom(
    "GET",
    toWizardEndpoint(`/sessions/${sessionId}/attachments/${attachmentId}`),
    {
      baseURL: WIZARD_BASE_URL,
      headers,
      responseType: "blob",
      timeoutMs: WIZARD_TIMEOUT_MS,
    }
  );
  if (blobResponse.code === 410) {
    throw new WizardAttachmentExpiredError();
  }
  if (blobResponse.code >= 400) {
    throw new Error(getWizardErrorMessage(blobResponse));
  }
  const objectUrl = URL.createObjectURL(blobResponse.result as Blob);
  return { url: objectUrl, revoke: () => URL.revokeObjectURL(objectUrl) };
}

let cachedWizardConfig: WizardClientConfig | null = null;

/** Fetch chat limits from the backend (cached). Backend is the source of truth. */
export async function getWizardConfig(): Promise<WizardClientConfig> {
  if (cachedWizardConfig) {
    return cachedWizardConfig;
  }
  const data = await wizardRequest("GET", "/config");
  cachedWizardConfig = {
    messageMaxChars: data.message_max_chars,
    pasteToFileThreshold: data.paste_to_file_threshold,
    attachmentMaxFileBytes: data.attachment_max_file_bytes,
    attachmentMaxPerMessage: data.attachment_max_per_message,
    attachmentAllowedContentTypes: Array.isArray(
      data.attachment_allowed_content_types
    )
      ? data.attachment_allowed_content_types
      : [],
  };
  return cachedWizardConfig;
}

export async function cancelWizardSession(
  sessionId: string
): Promise<WizardSessionResponse> {
  const response = await wizardRequest("POST", `/sessions/${sessionId}/cancel`);
  return mapWizardSessionResponse(response);
}

export async function getWizardSessionStatus(
  sessionId: string
): Promise<WizardStatusResponse> {
  const response = await wizardRequest("GET", `/sessions/${sessionId}/status`);
  return mapWizardStatusResponse(response);
}

export async function getWizardContextLevel(
  sessionId: string
): Promise<WizardContextLevel> {
  const response = await wizardRequest("GET", `/sessions/${sessionId}/context`);
  return mapWizardContextLevel(response);
}

/**
 * Compact this conversation's working memory now.
 *
 * Summarizes older history on the supervisor thread and on every sub-agent thread
 * the session owns. The user-visible transcript is not touched.
 */
export async function compactWizardSession(
  sessionId: string
): Promise<WizardCompactionResult> {
  const response = await wizardRequest(
    "POST",
    `/sessions/${sessionId}/compact`
  );
  return mapWizardCompactionResult(response);
}

export async function getWizardConversation(
  sessionId: string
): Promise<WizardConversationResponse> {
  const response = await wizardRequest(
    "GET",
    `/sessions/${sessionId}/conversation`
  );
  return mapWizardConversationResponse(response);
}

export async function getWizardSession(
  sessionId: string
): Promise<WizardSessionResponse> {
  const response = await wizardRequest("GET", `/sessions/${sessionId}`);
  return mapWizardSessionResponse(response);
}

export async function listWizardSessions(
  limit = 20,
  mode?: WizardMode,
  flowId?: string,
  flowVersion?: number,
  activityId?: string
): Promise<WizardSessionListResponse> {
  const response = await wizardRequest("GET", "/sessions", {
    params: {
      limit,
      mode,
      activity_id: activityId,
      flow_id: flowId,
      flow_version: flowVersion,
    },
  });
  return mapWizardSessionListResponse(response);
}

export async function getLatestWizardSessionForFlow(
  flowId: string,
  flowVersion: number
): Promise<WizardSessionListItem | null> {
  const response = await listWizardSessions(1, undefined, flowId, flowVersion);
  return response.sessions[0] || null;
}

// Finds a prior auto-debug session for one specific run (activityId), not
// just "the latest session touching this flow+version" — a single flow
// version can have many runs, so getLatestWizardSessionForFlow would return
// an unrelated run's conversation here. There's no backend filter keyed by
// activityId, so this matches on the run id appearing in the auto-debug seed
// text every such session is created with (see WIZARD_AUTO_DEBUG_SEED_PREFIX
// / buildDebugSeedMessage in WizardDrawer.vue) — the preview is the raw first
// user message (see sessions.py list_sessions), and activityId is a UUID, so
// a substring match is unambiguous regardless of the seed's exact wording.
// This is what lets a failed run's wizard recognize "already analyzed, saved
// in Elasticsearch" instantly (no re-analysis, no toast) even on a brand-new
// browser session where sessionStorage's scope cache is empty.
//
// Two extra guards beyond the substring match:
//  - isAutoDebugSeedText confirms the match is actually an auto-debug seed,
//    not a manually-typed chat message that happens to quote/paste this same
//    activityId (e.g. a user asking "why did run <uuid> fail?" in Chat mode)
//    — without it, that unrelated conversation would get silently hydrated
//    in as if it were the run's diagnosis.
//  - isWizardTerminalStatus excludes a session that's still "processing" (or
//    was orphaned mid-run) — reusing one of those would report
//    analysis-ready/reused:true and enable "Show analysis" before any real
//    diagnosis exists.
// Window size: the sessions endpoint caps `limit` at 100 and exposes NO offset/cursor
// (see list_sessions in api/routers/sessions.py), so paginating past it is impossible from
// here. Using the maximum minimises — but cannot remove — the risk that a run analysed long
// ago is pushed out of the window by >100 newer sessions on the same flow+version. Missing it
// is degrading, not incorrect: the caller simply starts a fresh analysis. Eliminating it needs
// a backend filter keyed by activityId (see the note above about there being none today).
const ACTIVITY_SESSION_LOOKUP_LIMIT = 100;

export async function getLatestWizardSessionForActivity(
  flowId: string,
  flowVersion: number,
  activityId: string
): Promise<WizardSessionListItem | null> {
  // Fast path: sessions are now anchored to their run via debug_context, which is an indexed
  // keyword, so the backend can answer this with one keyed query instead of shipping a whole
  // ACTIVITY_SESSION_LOOKUP_LIMIT-sized page for the browser to substring-scan (AI-268). Being
  // keyed, it also has none of the windowing risk described above.
  const keyed = await listWizardSessions(
    5,
    undefined,
    flowId,
    flowVersion,
    activityId
  );
  const keyedMatch = keyed.sessions.find((session) =>
    isWizardTerminalStatus(session.status)
  );
  if (keyedMatch) {
    return keyedMatch;
  }

  // Fallback for sessions created before debug_context existed — they carry the run id only in
  // the seed text, so they are invisible to the keyed query. Costs an extra (empty) query only
  // when the fast path misses, and can be deleted once no pre-upgrade sessions remain.
  const legacy = await listWizardSessions(
    ACTIVITY_SESSION_LOOKUP_LIMIT,
    undefined,
    flowId,
    flowVersion
  );
  return (
    legacy.sessions.find(
      (session) =>
        isAutoDebugSeedText(session.preview) &&
        session.preview?.includes(activityId) &&
        isWizardTerminalStatus(session.status)
    ) || null
  );
}

export async function linkWizardSessionToFlow(
  sessionId: string,
  flowContext: WizardFlowContext
): Promise<WizardSessionResponse> {
  const response = await wizardRequest(
    "POST",
    `/sessions/${sessionId}/flow-link`,
    {
      body: {
        flow_id: flowContext.flowId,
        flow_version: flowContext.flowVersion,
        flow_name: flowContext.flowName,
      },
    }
  );
  return mapWizardSessionResponse(response);
}

export async function resolveWizardFlowApproval(
  sessionId: string,
  options?: {
    flowContext?: WizardFlowContext | null;
    currentFlowSnapshot?: WizardCurrentFlowSnapshot | null;
  }
): Promise<WizardSessionResponse> {
  const response = await wizardRequest(
    "POST",
    `/sessions/${sessionId}/approval-resolved`,
    {
      body:
        options?.flowContext || options?.currentFlowSnapshot
          ? {
              ...(options?.flowContext
                ? {
                    flow_id: options.flowContext.flowId,
                    flow_version: options.flowContext.flowVersion,
                    flow_name: options.flowContext.flowName,
                  }
                : {}),
              ...(options?.currentFlowSnapshot
                ? { current_flow_snapshot: options.currentFlowSnapshot }
                : {}),
            }
          : undefined,
    }
  );
  return mapWizardSessionResponse(response);
}

/**
 * Save the wizard's generated flow via the Python backend.
 * Python reads final_flow from session state, preprocesses parameters/headers/
 * contentPath into the correct ActionDefinitionDto format, then calls Java.
 */
export async function saveWizardFlow(
  sessionId: string,
  flowContext?: WizardFlowContext | null,
  useNewUiEditor = false,
  flowSpec?: unknown
): Promise<PersistedWizardFlowResult> {
  const isUpdate =
    Boolean(flowContext?.flowId) &&
    typeof flowContext?.flowVersion === "number";
  // Pass the exact approved flow_spec so the backend persists it verbatim instead
  // of re-deriving the flow from session state (which can pick up a stale flow).
  const hasFlowSpec = Boolean(flowSpec) && typeof flowSpec === "object";
  const shouldSendBody = isUpdate || useNewUiEditor || hasFlowSpec;
  const response = await wizardRequest(
    "POST",
    `/sessions/${sessionId}/save-flow`,
    {
      body: shouldSendBody
        ? {
            ...(isUpdate
              ? {
                  flow_id: flowContext?.flowId,
                  flow_version: flowContext?.flowVersion,
                  flow_name: flowContext?.flowName,
                }
              : {}),
            ...(useNewUiEditor ? { new_ui: true } : {}),
            ...(hasFlowSpec ? { flow_spec: flowSpec } : {}),
          }
        : undefined,
    }
  );
  const flowData = response.flow || response;
  const rawFlowId = flowData?.id;
  const hasValidFlowId =
    typeof rawFlowId === "number" ||
    (typeof rawFlowId === "string" && rawFlowId.trim().length > 0);

  if (!hasValidFlowId) {
    const availableFields =
      flowData && typeof flowData === "object"
        ? Object.keys(flowData).join(", ") || "none"
        : typeof flowData;
    throw new Error(
      `Wizard save response did not include a valid flow id for session ${sessionId} ` +
        `(available fields: ${availableFields})`
    );
  }

  return {
    id: String(rawFlowId).trim(),
    version: Number(flowData.version || 1),
    name:
      (typeof flowData.name === "string" && flowData.name.trim()) ||
      "Untitled Flow",
    action: isUpdate ? "updated" : "created",
  };
}

function parseWizardStreamMessage(frame: Message): WizardStreamMessage | null {
  try {
    const payload = JSON.parse(frame.body);

    if (
      !payload ||
      typeof payload.sessionId !== "string" ||
      typeof payload.status !== "string"
    ) {
      return null;
    }

    return {
      sessionId: payload.sessionId,
      status: payload.status,
      message: typeof payload.message === "string" ? payload.message : "",
      timestamp:
        typeof payload.timestamp === "string" ? payload.timestamp : undefined,
      data:
        payload.data && typeof payload.data === "object" ? payload.data : {},
    };
  } catch {
    return null;
  }
}

export function isWizardTerminalStatus(status: string): boolean {
  return ["waiting_for_user", "completed", "error", "cancelled"].includes(
    status
  );
}

export function getWizardPhaseStatusMessage(currentPhase?: string): string {
  if (!currentPhase) {
    return "Brewing";
  }

  return WIZARD_PHASE_STATUS_MESSAGES[currentPhase] || "Brewing";
}

export function disconnectWizardStream(
  connection: WizardStreamConnection | null
): void {
  if (!connection) {
    return;
  }

  connection.subscription.unsubscribe();
  connection.client.disconnect();
}

export async function connectWizardSessionStream(
  sessionId: string,
  handlers: {
    onMessage: (message: WizardStreamMessage) => void;
    onError?: (error: Error | CloseEvent | any) => void;
  }
): Promise<WizardStreamConnection> {
  const client = await connectWebsocket();
  client.debug = () => undefined;

  return new Promise((resolve, reject) => {
    let settled = false;

    const handleError = (error: Error | CloseEvent | any) => {
      if (!settled) {
        reject(error);
        return;
      }

      if (handlers.onError) {
        handlers.onError(error);
      }
    };

    client.connect(
      {},
      () => {
        const subscription = client.subscribe(
          WIZARD_STOMP_DESTINATION,
          (frame: Message) => {
            const message = parseWizardStreamMessage(frame);

            if (!message || message.sessionId !== sessionId) {
              return;
            }

            handlers.onMessage(message);
          }
        );

        settled = true;
        resolve({
          client,
          subscription,
          disconnect: () => {
            subscription.unsubscribe();
            client.disconnect();
          },
        });
      },
      handleError
    );
  });
}
